import { BillingFeature } from '@/lib/billing/capabilities'
import { StreamKind } from '@/types/generated/common/account'
import { AnalyticsType } from '@/types/generated/common/types'
import { Capabilities } from '@/types/generated/public_api'

/**
 * A human-meaningful "what's included" row derived from a `Capabilities` message.
 * The pricing page renders these straight from `get_plans` so the comparison can
 * never drift from what the backend enforces (the §3 tier table is documentation
 * only). `count` rows show a number; `bool` rows show included / not-included.
 */
export type CapabilityHighlight =
  | { key: string; labelKey: string; kind: 'count'; count: number }
  | { key: string; labelKey: string; kind: 'bool'; value: boolean }

/**
 * `allowedFeatures` keys shown as included / not-included rows, in display order.
 * A key the backend adds later stays hidden until it gets a row and a label here.
 */
const FEATURE_ROWS: ReadonlyArray<[key: string, feature: BillingFeature]> = [
  ['riskSimulation', BillingFeature.RiskSimulation],
  ['algoBenchmarks', BillingFeature.AlgoBenchmarks],
  ['crossExchangeCharts', BillingFeature.CrossExchangeCharts],
  ['paradigm', BillingFeature.Paradigm],
  ['arbitrageScanner', BillingFeature.ArbitrageScanner],
  ['newsfeed', BillingFeature.Newsfeed],
  ['economicCalendar', BillingFeature.EconomicCalendar],
  ['aiPromptTrading', BillingFeature.AiPromptTrading],
  ['aiResearchAgents', BillingFeature.AiResearchAgents],
  ['dataApi', BillingFeature.DataApi],
]

export function capabilityHighlights(caps: Capabilities): CapabilityHighlight[] {
  const streams = new Set(caps.allowedStreamKinds)
  const analytics = new Set(caps.allowedAnalyticsTypes)
  const features = new Set(caps.allowedFeatures)

  return [
    {
      key: 'tradingAccounts',
      labelKey: 'features.tradingAccounts',
      kind: 'count',
      count: caps.maxAccounts,
    },
    {
      key: 'accountSharing',
      labelKey: 'features.accountSharing',
      kind: 'count',
      count: caps.maxSharedMembers,
    },
    { key: 'venues', labelKey: 'features.venues', kind: 'count', count: caps.allowedVenues.length },
    {
      key: 'algoTypes',
      labelKey: 'features.algoTypes',
      kind: 'count',
      count: caps.allowedAlgos.length,
    },
    {
      key: 'executionAnalytics',
      labelKey: 'features.executionAnalytics',
      kind: 'bool',
      value: streams.has(StreamKind.STREAM_KIND_EXECUTION_ANALYTICS),
    },
    {
      key: 'optionsFlow',
      labelKey: 'features.optionsFlow',
      kind: 'bool',
      value: streams.has(StreamKind.STREAM_KIND_OPTIONS_FLOW),
    },
    {
      key: 'impliedVsRealized',
      labelKey: 'features.impliedVsRealized',
      kind: 'bool',
      value: analytics.has(AnalyticsType.ANALYTICS_TYPE_IMPLIED_VS_REALIZED),
    },
    ...FEATURE_ROWS.map(
      ([key, feature]): CapabilityHighlight => ({
        key,
        labelKey: `features.${key}`,
        kind: 'bool',
        value: features.has(feature),
      })
    ),
  ]
}

/**
 * Whether the user's capability highlights already satisfy a plan's highlight —
 * drives the "✓ included in your plan" vs "↑ upgrade to unlock" marker. Bool rows
 * must be present; count rows must meet or exceed the plan's number. The list is
 * a fixed handful of rows, so a linear lookup is plenty.
 */
export function isHighlightSatisfied(
  userHighlights: CapabilityHighlight[],
  planHighlight: CapabilityHighlight
): boolean {
  const mine = userHighlights.find((row) => row.key === planHighlight.key)
  if (!mine) return false
  if (planHighlight.kind === 'bool') return mine.kind === 'bool' && mine.value
  return mine.kind === 'count' && mine.count >= planHighlight.count
}
